import { Divider } from "@blueprintjs/core";
import { ViewId, UserType } from "@madhive/mad-sdk";
import { uniqBy } from "lodash";
import { selectProductionViews } from "appReducers/authReducer";
import { useSelector } from "react-redux";
import { css } from "@emotion/react";
import { Drawer } from "frontier/lib/components";
import { Button, Icons } from "@madhive/kit-ui";
import ListItem from "frontier/lib/components/List/ListItem";
import { FC, useMemo, Fragment, MouseEvent, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import {
  mapViewIdToView,
  findActiveViewHref,
  SORT_INDEX_FOR_UNKNOWN_VIEW,
  OrderedViewCategories,
  ViewCategories
} from "./Router/utils";
import { AllowedOnly } from "./Router/WithAuth";
import InternalViewLink from "./InternalViewLink";
import ExternalToolLink from "./ExternalToolLink";
import ViewLink from "./ViewLink";
import { useMainMenuViews } from "./main-menu/useMainMenuViews";
import { getPrismHost } from "frontier/lib/prism/prismHost";
import { madSDK } from "lib/sdk";

/**
 * Smithers' host rewrites this path to edge, which fronts Prism. The
 * trailing slash is required: edge registers the route as
 * `/data-intelligence/` (see common/edgeheaders.DataIntelligencePathPrefix),
 * and its router treats `/x` and `/x/` as distinct registrations, so a bare
 * `/data-intelligence` falls through instead of reaching Prism.
 */
const PRISM_DATA_INTELLIGENCE_PATH = "/data-intelligence/";

export const style = {
  menuDrawer: css`
    .DrawerCloseContainer {
      min-height: 64px;
      background-color: var(--white);
    }
  `,
  activeStyle: css`
    color: var(--black);
    ::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      border-left: 2px solid var(--color-splash-primary);
      border-radius: var(--spacing-4);
    }
  `,
  listContainer: (active: boolean = false) => css`
    padding: var(--spacing-8) var(--spacing-24);
    color: var(--color-secondary);
    ${active ? style.activeStyle : undefined}
  `,
  fullList: css`
    width: auto;
  `,
  linkText: css`
    padding: 0;
    color: var(--color-secondary);
    text-decoration: none !important;
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-12);
  `,
  listItemIcon: css`
    min-width: 40px !important;
    color: var(--semi-dark);
  `,
  close: css`
    font-size: 2em;
  `,
  categoryHeading: css`
    font-size: var(--font-size-12);
  `,
  divider: css`
    margin: 0px;
  `,
  listItemContainer: css`
    padding: var(--spacing-24) var(--spacing-24) var(--spacing-8)
      var(--spacing-24);
  `,
  sectionHeading: css`
    border: none;
    cursor: default;
    color: var(--color-secondary);
  `,
  deepWaterHeading: css`
    font-size: var(--font-size-12);
    margin-top: 1em !important;
    padding: 0 var(--spacing-24);
  `
};

const ViewHeading: FC<PropsWithChildren> = props => {
  const { children } = props;
  // If there is no category, don't render the heading.
  if (!children) {
    return null;
  }
  return (
    <p
      css={[
        style.sectionHeading,
        style.categoryHeading,
        style.listItemContainer
      ]}
    >
      {children}
    </p>
  );
};

interface ViewSelectorDrawerProps {
  isOpen: boolean;
  views: ViewId[];
  width: number;
  onDrawerClose: () => void;
}

const ViewSelectorDrawer: FC<ViewSelectorDrawerProps> = ({
  views,
  isOpen,
  onDrawerClose,
  width
}) => {
  const orgProductionViews = useSelector(selectProductionViews);
  const { views: menuViews, internalTools } = useMainMenuViews(views);

  // Called once here so useLocation doesn't get called twice and violate hook rules
  const currentPath = useLocation().pathname;

  /** Deduplicate any views that lead to the same URL. */
  const hydratedViews = useMemo(
    () =>
      uniqBy(
        menuViews.map(viewId => ({
          viewId,
          ...mapViewIdToView(viewId, orgProductionViews)
        })),
        view => view.href
      )
        .filter(view => view.sortIndex !== SORT_INDEX_FOR_UNKNOWN_VIEW)
        .filter(view => view.category !== ViewCategories.UNKNOWN),
    [menuViews, orgProductionViews]
  );

  // Derived from the route rather than tracked via clicks, so navigating in
  // from anywhere other than this sidebar (e.g. a Link on the page itself)
  // still highlights the right item.
  const activeItem = useMemo(
    () =>
      findActiveViewHref(
        currentPath,
        hydratedViews.map(view => view.href)
      ),
    [currentPath, hydratedViews]
  );

  // On Smithers' own origin the href is enough: Prism reads the session from
  // the MAD-AUTH cookie. Vite has no /data-intelligence rewrite, so local dev
  // goes to the API host, another origin, and hands the token off as ?token=,
  // which Prism strips from the URL.
  const openDataIntelligence = (e: MouseEvent<HTMLAnchorElement>) => {
    onDrawerClose();
    if (!import.meta.env.DEV) {
      return;
    }
    e.preventDefault();
    const token = madSDK.authentication.getAuthorizationToken();
    const url = `${getPrismHost()}${PRISM_DATA_INTELLIGENCE_PATH}`;
    window.location.assign(
      token ? `${url}?token=${encodeURIComponent(token)}` : url
    );
  };

  /** Generate a mapping of ordered categories to its view links */
  const categoriesToViews = useMemo(
    () =>
      OrderedViewCategories.reduce((mapping, category) => {
        const orderedViewsInCategory = hydratedViews
          .filter(view => view.category === category)
          .sort((viewA, viewB) => viewA.sortIndex - viewB.sortIndex);

        if (orderedViewsInCategory.length) {
          return mapping.set(category, orderedViewsInCategory);
        }
        return mapping;
      }, new Map()),
    [hydratedViews]
  );

  const dataIntelligenceLink = (
    <ExternalToolLink
      id="admin-data-intelligence-platform-link"
      href={PRISM_DATA_INTELLIGENCE_PATH}
      newTab={false}
      text="Data Intelligence Platform"
      onClick={openDataIntelligence}
    />
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onDrawerClose}
      position="left"
      size="315px"
      css={style.menuDrawer}
    >
      <div
        role="menubar"
        aria-label="main menu"
        css={css`
          width: ${width};
        `}
      >
        <ListItem
          className="DrawerCloseContainer"
          css={style.listItemContainer}
        >
          <Button
            variant="ghost"
            prefix={
              <Icons.Arrow direction="left" fill="var(--color-secondary)" />
            }
            tabIndex={0}
            onClick={onDrawerClose}
            ariaLabel="close menu"
          />
        </ListItem>
        <Divider css={style.divider} />
        {[...categoriesToViews].map(
          ([category, categoryViews]: [
            string,
            Array<{
              href: string;
              viewId: string;
              icon: JSX.Element;
              title: string;
            }>
          ]) => (
            <Fragment key={category}>
              <ViewHeading>{category}</ViewHeading>
              {categoryViews.map(view => (
                <ViewLink
                  key={view.viewId}
                  activeItem={activeItem}
                  onClick={onDrawerClose}
                  {...view}
                />
              ))}
              {category === ViewCategories.ADMIN && (
                <AllowedOnly userType={UserType.DEV}>
                  {dataIntelligenceLink}
                </AllowedOnly>
              )}
            </Fragment>
          )
        )}
        {!categoriesToViews.has(ViewCategories.ADMIN) && (
          <AllowedOnly userType={UserType.DEV}>
            <ViewHeading>{ViewCategories.ADMIN}</ViewHeading>
            {dataIntelligenceLink}
          </AllowedOnly>
        )}
        <AllowedOnly userType={UserType.DEV}>
          <ViewHeading>Internal Tools</ViewHeading>
          {internalTools.map(view => (
            <InternalViewLink
              key={view.route}
              to={view.route}
              text={view.name}
              onClick={onDrawerClose}
            />
          ))}
        </AllowedOnly>
      </div>
    </Drawer>
  );
};

export default ViewSelectorDrawer;
